/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';
import { Col, Form, Row, Switch } from 'antd';
import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import locale from 'antd/es/date-picker/locale/pt_BR';
import localeEs from 'antd/es/date-picker/locale/es_ES';
import { Input, InputDate } from '@components/input';
import {
    orderItemsSelector,
    orderTypeSelector,
    showModalBranch,
    updateOrder,
} from '@redux/slices/order';
import { FormItem, Select, Option } from '@styles/components/form';
import { useQuery as useQueryHooks } from '@hooks/use-query';
import { fetchCampaignsForOrder, selectCampaign } from '@redux/slices/campaign';
import { isEsAr, translate } from '@components/i18n';
import { isEsMx } from '@components/i18n';
import { isEsCo } from '@components/i18n';
import { isEsPe } from '@components/i18n';
import { SHOW_DELIVERY_DATE, ENABLE_PAYMENT_CONDITION, DO_NOT_SHOW_MONEY_SELECTION } from '@config/env';
import { resetAvailableItems, updatePaymentCondition } from '@redux/slices/order/select-items';

interface SelectItemsHeaderProps {
    readOnly?: boolean;
    marketing?: boolean;
    bonification?: boolean;
}

export const SelectItemsFilters: React.FC<SelectItemsHeaderProps> = memo(({ bonification, marketing }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const query = useQueryHooks();
    const selectItemsState = useSelector(orderItemsSelector);
    const selectOrderTypeSelector = useSelector(orderTypeSelector);
    const isBonification = selectItemsState.order?.paymentCondition?.id === '100'

    useEffect(() => {
        if (!form.getFieldValue("paymentConditionId")) {
            form.setFieldsValue({
                paymentConditionId: selectItemsState.order?.paymentCondition?.id,
            });
        }
    }, [selectItemsState.order.paymentCondition])


    const onValuesChange = (changedValues: any, allValues: any): void => {
        if (changedValues.paymentConditionId) {
            const paymentCondition = selectItemsState
                .availablePaymentConditions.find((c) => `${c.id}` === `${changedValues.paymentConditionId}`
                );
            if (!paymentCondition) return;

            setTimeout(() => {
                dispatch(updatePaymentCondition(paymentCondition)); // causa uma nova busca por items
            }, 300);
        } else {
            if (!changedValues.addCustomer) {
                // verify if the platform is equal to platform copy. If not, show modal to select platform again
                if (selectOrderTypeSelector?.orderType?.platformCopy?.id && selectOrderTypeSelector?.orderType?.platformCopy?.id !== selectOrderTypeSelector?.orderType?.platform.id) {
                    dispatch(showModalBranch())
                }
            }
            dispatch(updateOrder(allValues));
        }
    };

    const updateScheduledDate = (date: any): void => {
        if (!date) return

        dispatch(
            updateOrder({
                ...selectItemsState.order,
                orderType: {
                    ...selectItemsState.order.orderType,
                    date,
                },
            }),
        );

        if (selectItemsState?.order?.paymentCondition?.id) {
            dispatch(
                fetchCampaignsForOrder({
                    paymentConditionId: selectItemsState.order.paymentCondition.id,
                    date,
                }),
            );
        }
    };

    if (bonification || marketing) {

        const schedule = bonification && marketing && query.get('scheduled')

        return (
            <Row justify='end' style={{ width: '100%' }}>
                <Form
                    form={form}
                    layout="inline"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 24 }}
                    style={{ width: '100%', display: 'flex', justifyContent: 'end' }}
                    onValuesChange={onValuesChange}
                    initialValues={{
                        ...selectItemsState.order,
                        moneyType: selectItemsState?.selectedMoeda?.codigo,
                        paymentConditionId:
                            selectItemsState.order?.paymentCondition?.id,
                        deliveryDate: selectItemsState.order?.deliveryDate
                            ? moment(selectItemsState.order?.deliveryDate)
                            : undefined,
                        order: selectItemsState.order.order,
                    }}
                >
                    <Col xs={24} md={schedule ? 12 : 24}>
                        <FormItem
                            label={translate('forms.labels.buyOrder')}
                            name={'order'}
                        >
                            <Input
                                placeholder={translate(
                                    'forms.placeholders.writeTheData',
                                )}
                            />
                        </FormItem>
                    </Col>
                    {schedule && (
                        <Col xs={24} md={12}>
                            <FormItem
                                label={translate('forms.labels.scheduledDate')}
                            >
                                <InputDate
                                    locale={isEsAr() || isEsMx() || isEsCo() || isEsPe() ? localeEs : locale}
                                    format={'DD/MM/YYYY'}
                                    disabledDate={(current) => {
                                        return moment() >= current;
                                    }}
                                    placeholder={translate(
                                        'forms.placeholders.chooseTheDate',
                                    )}
                                    defaultValue={
                                        selectItemsState.order.orderType.date
                                            ? moment(
                                                selectItemsState.order.orderType.date,
                                            )
                                            : undefined
                                    }
                                    onChange={updateScheduledDate}
                                />
                            </FormItem>
                        </Col>
                    )}
                </Form>
            </Row>
        )
    }

    return (
        <Row justify='end' style={{ width: '100%' }}>
            <Form
                form={form}
                layout="inline"
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 24 }}
                style={{ width: '100%', display: 'flex', justifyContent: 'end' }}
                onValuesChange={onValuesChange}
                initialValues={{
                    ...selectItemsState.order,
                    moneyType: selectItemsState?.selectedMoeda?.codigo,
                    paymentConditionId:
                        selectItemsState.order?.paymentCondition?.id,
                    deliveryDate: selectItemsState.order?.deliveryDate
                        ? moment(selectItemsState.order?.deliveryDate)
                        : undefined,
                }}
            >
                <Row justify='end' style={{ width: '100%' }}>
                    {!DO_NOT_SHOW_MONEY_SELECTION && (
                        <Col xs={24} md={12} lg={6} xxl={5}>
                            <FormItem
                                label={translate('forms.labels.moneyType')}
                                name={'moneyType'}
                            >
                                <Select
                                    placeholder={translate('forms.placeholders.chooseOption')}
                                    allowClear
                                    disabled
                                    value={selectItemsState?.selectedMoeda?.codigo}
                                >
                                    {selectItemsState.moedas.map((t) => (
                                        <Option key={t.codigo} value={t.codigo}>
                                            {`${t.codigo} - ${t.descricao}`}
                                        </Option>
                                    ))}
                                </Select>
                            </FormItem>
                        </Col>
                    )}
                    <Col xs={24} md={12} lg={6} xxl={5}>
                        <FormItem
                            label={translate('forms.labels.buyOrder')}
                            name={'order'}
                        >
                            <Input
                                placeholder={translate(
                                    'forms.placeholders.writeTheData',
                                )}
                            />
                        </FormItem>
                    </Col>
                    <Col xs={24} md={12} lg={6} xxl={5}>
                        <FormItem
                            label={translate('forms.labels.paymentCondition')}
                            name={'paymentConditionId'}
                        >
                            <Select
                                placeholder={translate(
                                    'forms.placeholders.chooseOption',
                                )}
                                loading={
                                    selectItemsState.isFetchingPaymentCondition
                                }
                                dropdownMatchSelectWidth={false}
                                disabled={(!ENABLE_PAYMENT_CONDITION || isBonification)}
                            >
                                {selectItemsState.availablePaymentConditions
                                    .filter((c) => `${c.id}` !== `100`)
                                    .map((t) => (
                                        <Option key={t.id} value={t.id}>
                                            {t.description}
                                        </Option>
                                    ))}
                            </Select>
                        </FormItem>
                    </Col>
                    {SHOW_DELIVERY_DATE && (
                        <Col xs={24} md={12} lg={6} xxl={4}>
                            <FormItem
                                label={translate('forms.labels.deliveryDate')}
                                name={'deliveryDate'}
                            >
                                <InputDate
                                    locale={isEsAr() || isEsMx() || isEsCo() || isEsPe() ? localeEs : locale}
                                    format={'DD/MM/YYYY'}
                                    disabledDate={(current) => {
                                        return moment() >= current;
                                    }}
                                    placeholder={translate(
                                        'forms.placeholders.chooseTheDate',
                                    )}
                                />
                            </FormItem>
                        </Col>
                    )}
                    <Col xs={24} md={12} lg={5} xxl={3}>
                        <FormItem label={translate("general.deliveryClient")} name="addCustomer">
                            <Switch
                                checked={selectItemsState.order?.addCustomer}
                            />
                        </FormItem>
                    </Col>
                    {query.get('scheduled') && (
                        <Col xs={24} md={12} lg={5} xxl={4}>
                            <FormItem
                                label={translate('forms.labels.scheduledDate')}
                            >
                                <InputDate
                                    locale={isEsAr() || isEsMx() || isEsCo() || isEsPe() ? localeEs : locale}
                                    format={'DD/MM/YYYY'}
                                    disabledDate={(current) => {
                                        return moment() >= current;
                                    }}
                                    placeholder={translate(
                                        'forms.placeholders.chooseTheDate',
                                    )}
                                    defaultValue={
                                        selectItemsState.order.orderType.date
                                            ? moment(
                                                selectItemsState.order.orderType.date,
                                            )
                                            : undefined
                                    }
                                    onChange={updateScheduledDate}
                                />
                            </FormItem>
                        </Col>
                    )}
                </Row>
            </Form>
        </Row>
    );
});
